import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Margin } from 'styled-components-spacing';
import { Heading, Table } from 'components';
import { formatDate } from 'helpers/date';
import { FileList } from './YoungCrewSection.styled';

/**
 * What the Young Crew of this country reported for the same year, read as one
 * more category of the Member Association report - same heading, same tables,
 * same way of reading a label against a value.
 *
 * Figures appear only once the YC Management Board accepted that report, so a
 * Member Association reads what was reviewed rather than a draft in progress.
 * The category itself is always here though, note and all: a Member Association
 * filling in its own report has to be able to see the part it does not own, and
 * read exactly what the office will be shown. Everything is read only - the
 * Young Crew owns this data - and the documents are links straight to the
 * stored file, so whoever reviews the report downloads them without leaving the
 * screen.
 */
const YoungCrewSection = ({ youngCrew }) => {
  const { t } = useTranslation();

  if (!youngCrew) return null;

  // Older payloads carried the accepted report and nothing else.
  const state = youngCrew.state || 'accepted';
  const accepted = state === 'accepted';
  const crew = youngCrew.youngCrew || {};
  const crewName = crew.name || crew.initials || '';
  const globalEvents = youngCrew.globalEvents || [];
  const globalEventsOnRecord = youngCrew.globalEventsOnRecord || [];

  const renderFiles = (files) =>
    files.length ? (
      <FileList>
        {files.map((file) => (
          <li key={file.id}>
            <a href={file.url} target="_blank" rel="noopener noreferrer">
              {file.name}
            </a>
          </li>
        ))}
      </FileList>
    ) : (
      '-'
    );

  const renderTags = (events) => (events.length ? events.map((e) => e.label).join(', ') : '-');

  const renderRow = (label, value) => (
    <Table.Row key={label}>
      <Table.Cell style={{ fontWeight: 600, width: '300px' }}>{label}</Table.Cell>
      <Table.Cell>
        <Table.Label>{label}:</Table.Label>
        {value}
      </Table.Cell>
    </Table.Row>
  );

  /** The overview, read the same way as the numbers of every other category. */
  const renderSummaryTable = () => {
    const rows = [renderRow(t('page.ma.youngCrewSection.youngCrew'), crewName || '-')];

    if (accepted) {
      rows.push(
        renderRow(
          t('page.ma.youngCrewSection.acceptedOn'),
          youngCrew.acceptedAt ? formatDate(youngCrew.acceptedAt) : '-',
        ),
        renderRow(t('page.ma.youngCrewSection.eventsCount'), youngCrew.eventsCount),
        renderRow(t('page.ma.youngCrewSection.participantsTotal'), youngCrew.participantsTotal),
        renderRow(t('page.ma.youngCrewSection.yearlyPlan'), renderFiles(youngCrew.yearlyPlan)),
        renderRow(t('page.ma.youngCrewSection.yearlyBudget'), renderFiles(youngCrew.yearlyBudget)),
        renderRow(t('page.ma.youngCrewSection.globalEventsStated'), renderTags(globalEvents)),
      );
    }

    // What the board recorded is its own data, not the Young Crew's, so it
    // still has something to say in a year with no accepted report.
    if (accepted || globalEventsOnRecord.length) {
      rows.push(
        renderRow(
          t('page.ma.youngCrewSection.globalEventsOnRecord'),
          renderTags(globalEventsOnRecord),
        ),
      );
    }

    return (
      <Table tablebreakpoint="lg">
        <Table.Head>
          <Table.Row>
            <Table.Heading />
            <Table.Heading>{t('page.ma.youngCrewSection.value')}</Table.Heading>
          </Table.Row>
        </Table.Head>
        <Table.Body>{rows}</Table.Body>
      </Table>
    );
  };

  const renderEventsTable = () => (
    <Table tablebreakpoint="lg">
      <Table.Head>
        <Table.Row>
          <Table.Heading>{t('page.ma.youngCrewSection.eventName')}</Table.Heading>
          <Table.Heading>{t('page.ma.youngCrewSection.eventDate')}</Table.Heading>
          <Table.Heading>{t('page.ma.youngCrewSection.eventDelivery')}</Table.Heading>
          <Table.Heading>{t('page.ma.youngCrewSection.eventParticipants')}</Table.Heading>
          <Table.Heading>{t('page.ma.youngCrewSection.eventProof')}</Table.Heading>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {youngCrew.events.map((event, index) => (
          // Events of one report have no id of their own in this payload, and
          // their order is the order the Young Crew entered them in.
          // eslint-disable-next-line react/no-array-index-key
          <Table.Row key={`${event.name}-${index}`}>
            <Table.Cell style={{ fontWeight: 600 }}>
              <Table.Label>{t('page.ma.youngCrewSection.eventName')}:</Table.Label>
              {event.name || '-'}
            </Table.Cell>
            <Table.Cell>
              <Table.Label>{t('page.ma.youngCrewSection.eventDate')}:</Table.Label>
              {event.date ? formatDate(event.date) : '-'}
            </Table.Cell>
            <Table.Cell>
              <Table.Label>{t('page.ma.youngCrewSection.eventDelivery')}:</Table.Label>
              {event.modeOfDelivery || '-'}
            </Table.Cell>
            <Table.Cell>
              <Table.Label>{t('page.ma.youngCrewSection.eventParticipants')}:</Table.Label>
              {event.participants}
            </Table.Cell>
            <Table.Cell>
              <Table.Label>{t('page.ma.youngCrewSection.eventProof')}:</Table.Label>
              {renderFiles(event.proof)}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );

  // Every state says its own piece, in the same place a category of this form
  // explains itself - so an empty category reads as an answer rather than as
  // something that failed to load.
  const descriptionKeys = {
    accepted: 'page.ma.youngCrewSection.description',
    pending: 'page.ma.youngCrewSection.pendingDescription',
    no_report: 'page.ma.youngCrewSection.noReportDescription',
    no_young_crew: 'page.ma.youngCrewSection.noYoungCrewDescription',
  };

  const description = t(descriptionKeys[state] || descriptionKeys.accepted, {
    youngCrew: crewName,
    year: youngCrew.year,
    date: youngCrew.acceptedAt ? formatDate(youngCrew.acceptedAt) : '-',
  });

  return (
    <Margin bottom="36">
      <Margin bottom="24">
        <Heading size="h4">{t('page.ma.youngCrewSection.title')}</Heading>
        <div style={{ marginTop: '10px' }}>{description}</div>
        {/* The Member Association does not own any of this, but it is part of
            what the office reads, so it is spelled out rather than implied. */}
        <div style={{ marginTop: '10px' }}>{t('page.ma.youngCrewSection.readOnly')}</div>
      </Margin>

      {renderSummaryTable()}

      {accepted && youngCrew.events.length ? (
        <Margin top="32">
          <Margin bottom="16">
            <Heading size="h5">{t('page.ma.youngCrewSection.events')}</Heading>
          </Margin>
          {renderEventsTable()}
        </Margin>
      ) : null}
    </Margin>
  );
};

const fileShape = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  url: PropTypes.string,
});

const eventTagShape = PropTypes.shape({
  value: PropTypes.string,
  label: PropTypes.string,
});

YoungCrewSection.propTypes = {
  youngCrew: PropTypes.shape({
    state: PropTypes.oneOf(['accepted', 'pending', 'no_report', 'no_young_crew']),
    youngCrew: PropTypes.shape({
      name: PropTypes.string,
      initials: PropTypes.string,
    }),
    year: PropTypes.number,
    acceptedAt: PropTypes.string,
    yearlyPlan: PropTypes.arrayOf(fileShape),
    yearlyBudget: PropTypes.arrayOf(fileShape),
    eventsCount: PropTypes.number,
    participantsTotal: PropTypes.number,
    events: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        date: PropTypes.string,
        modeOfDelivery: PropTypes.string,
        participants: PropTypes.number,
        proof: PropTypes.arrayOf(fileShape),
      }),
    ),
    participatedInGlobalEvents: PropTypes.bool,
    globalEvents: PropTypes.arrayOf(eventTagShape),
    globalEventsOnRecord: PropTypes.arrayOf(eventTagShape),
  }),
};

YoungCrewSection.defaultProps = {
  youngCrew: null,
};

export default YoungCrewSection;
